import React, { useEffect, useRef, useState } from 'react';
import { Spinner as ReactSpinner, Row } from 'react-bootstrap';
import {
  ActionDropdown,
  FileUpload,
  FileUploadDisplay,
  SalvButton,
  SalvTextArea,
  SidePanel,
  Size,
} from '@salvapp/ui-components';
import Message, { getStructuredMessage, JsonMessage, JsonMessageTypeEnum } from './Message';
import {
  BridgeApiConversation,
  BridgeApiConversationStatus,
  BridgeApiConversationTypeEnum,
  BridgeApiTemplate,
  InternalApiAlertRfi,
  InternalApiAlertRfiMessage,
  InternalApiAlertRfiMessageType,
  InternalApiAlertType,
} from '../api-internal';
import {
  alertRfiMessageApi,
  alertStatusApi,
  attachmentApi,
  conversationApi,
  monitoringAlertInternalApi,
} from '../apis';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import * as Yup from 'yup';
import { updateScreeningAlertSuccess } from '../screening/screeningReducer';
import { useDispatch } from 'react-redux';
import './ThreadSidePanel.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-regular-svg-icons';
import StructuredMessageForm from './StructuredMessageForm';
import { ACCEPTED_EXTENSION_TYPES, counterpartiesNames, DecryptedMessage } from './helper';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../organisation/feature/helper';
import { updateAlertSuccess } from '../alerts/alertReducer';
import { faBuildingColumns, faCircleCheck, faPaperPlane, faPlus } from '@fortawesome/pro-solid-svg-icons';
import AddOrganisationToConversationModal from './AddOrganisationToConversationModal';
import { ConversationConclusionModal } from './conversation-conclusion/ConversationConclusionModal';
import { faPenToSquare } from '@fortawesome/pro-solid-svg-icons/faPenToSquare';
import { useMe } from '../organisation/useMe';

const capitalizeFirstLetter = (value: string | undefined) => {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

export interface ThreadSidePanelProps {
  alertRfi: InternalApiAlertRfi;
  show: boolean;
  onComplete: (updated: boolean) => void;
  template?: BridgeApiTemplate;
  conversation: BridgeApiConversation;
}

const validationSchema = Yup.object().shape(
  {
    message: Yup.string().required(''),
  },
  []
);

export interface RfiMessageFormValues {
  message: string;
}

export function renderMessage(
  message: DecryptedMessage,
  template: BridgeApiTemplate | undefined,
  alertRfi: InternalApiAlertRfi,
  showRequestedFields: boolean
) {
  if (message.type === InternalApiAlertRfiMessageType.SYSTEM) {
    return (
      <div className='text-s system-message' key={message.id}>
        {message.content}
      </div>
    );
  } else {
    return (
      <Message
        key={message.id}
        message={message}
        template={template}
        customFields={alertRfi.requestedFields ? alertRfi.requestedFields.custom : []}
        counterparties={alertRfi.counterparties}
        requestedFields={showRequestedFields ? alertRfi.requestedFields : undefined}
      />
    );
  }
}

export async function findAllMessagesForAlertRfi(alertRfiId: string): Promise<InternalApiAlertRfiMessage[]> {
  let page = 0;
  const limit = 50;

  let hasNextPage = true;

  let allMessages: InternalApiAlertRfiMessage[] = [];

  while (hasNextPage) {
    const messagesPage = await alertRfiMessageApi.findAllAlertRfiMessages({
      page: page,
      size: limit,
      alertRfiId: alertRfiId,
    });

    allMessages = allMessages.concat(messagesPage.content);

    hasNextPage = messagesPage.total > allMessages.length && messagesPage.size != 0;
    page += 1;
  }

  return allMessages;
}

export const ThreadSidePanel = ({ show, onComplete, alertRfi, template, conversation }: ThreadSidePanelProps) => {
  const dispatch = useDispatch();

  const [messages, setMessages] = useState<InternalApiAlertRfiMessage[]>();
  const [loading, setLoading] = useState(false);
  const [showAddParticipantModal, setShowAddParticipantModal] = useState<boolean>(false);

  const [messageAttachments, setMessageAttachments] = useState<File[]>([]);

  const [showConversationConclusionModal, setShowConversationConclusionModal] = useState(false);
  const [isConclusionEditing, setConclusionEditing] = useState(false);

  const messagesFeedRef = useRef(null);
  const addAttachment = async (files: File[]) => {
    setMessageAttachments(files);
  };

  const removeFile = (index: number) => {
    const newFiles = [...messageAttachments];
    newFiles.splice(index, 1);
    setMessageAttachments(newFiles);
  };

  const { features } = useMe();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const messages = await findAllMessagesForAlertRfi(alertRfi.id);
      setMessages(messages.reverse());
      setLoading(false);
    };
    show && fetchData();
  }, [show]);

  const initialValues: RfiMessageFormValues = {
    message: '',
  };

  useEffect(() => {
    if (messagesFeedRef.current) {
      // @ts-ignore
      messagesFeedRef.current.scrollTop = messagesFeedRef.current.scrollHeight;
    }
  }, [messages]);

  const updateAlert = async (alertId: number, alertType: InternalApiAlertType) => {
    if (alertType === InternalApiAlertType.SCREENING) {
      await dispatch(updateScreeningAlertSuccess());
    } else if (alertType === InternalApiAlertType.MONITORING) {
      const updatedAlert = await monitoringAlertInternalApi.getAlertInternal({
        alertId: alertId,
      });
      await dispatch(updateAlertSuccess(updatedAlert));
    }
    onComplete(true);
  };

  const addMessageAndUpdateAlert = async (values: RfiMessageFormValues) => {
    const uploadedAttachments = await Promise.all(
      messageAttachments.map(async (attachment) => {
        return await attachmentApi.uploadMessageAttachment({
          file: attachment,
          conversationId: alertRfi.conversationId,
        });
      })
    );

    await alertRfiMessageApi.addMessage({
      alertRfiId: alertRfi.id,
      internalApiCreateAlertRfiMessage: {
        content: values.message,
        attachmentIds: uploadedAttachments.map((attachment) => attachment.handle!),
      },
    });
    await updateAlert(alertRfi.alertId, alertRfi.alertType);
  };

  const submit = async (
    values: RfiMessageFormValues,
    { setSubmitting, resetForm }: FormikHelpers<RfiMessageFormValues>
  ) => {
    try {
      setSubmitting(true);
      await addMessageAndUpdateAlert(values);
      resetForm();
      onComplete(true);
    } finally {
      setSubmitting(false);
    }
  };

  const changeStatus = async (status: BridgeApiConversationStatus) => {
    await conversationApi.updateConversation({
      id: alertRfi.conversationId,
      bridgeApiUpdateConversation: {
        status,
      },
    });
    if (status === BridgeApiConversationStatus.OBSERVING) {
      await alertStatusApi.updateAlertStatus({
        internalApiAlertStatusUpdate: {
          alertId: alertRfi.alertId,
          alertType: alertRfi.alertType,
          status: 'BRIDGE_OBSERVING',
        },
      });
      await updateAlert(alertRfi.alertId, alertRfi.alertType);
    }
  };

  const sendAndClose = async (values: RfiMessageFormValues, setSubmitting: any, resetForm: any) => {
    try {
      setSubmitting(true);
      await addMessageAndUpdateAlert(values);
      await changeStatus(BridgeApiConversationStatus.CLOSED);
      resetForm();
      onComplete(true);
    } finally {
      setSubmitting(false);
    }
  };

  function hasRequestedFields(): boolean {
    return (
      conversation.requestedFields !== undefined &&
      (conversation.requestedFields.fromTemplate.length !== 0 || conversation.requestedFields?.custom.length !== 0)
    );
  }

  function isStructuredRequestAnswered(): boolean {
    if (!messages) return true;
    return (
      messages.filter(
        (message) =>
          message.type === InternalApiAlertRfiMessageType.SENT && getStructuredMessage(message.content) !== undefined
      ).length >= 1
    );
  }

  const showStructuredRequest = () => {
    return (
      messages?.length !== 0 &&
      hasRequestedFields() &&
      !isStructuredRequestAnswered() &&
      conversation.type === BridgeApiConversationTypeEnum.RECIPIENT
    );
  };

  const footer = () => {
    if (
      loading ||
      conversation.status === BridgeApiConversationStatus.CLOSED ||
      conversation.status === BridgeApiConversationStatus.OBSERVING
    )
      return null;
    return (
      <div className='new-message'>
        <Formik initialValues={initialValues} onSubmit={submit} validationSchema={validationSchema} validateOnMount>
          {({
            isSubmitting,
            isValid,
            values,
            setSubmitting,
            resetForm,
            setFieldValue,
            errors,
          }: FormikProps<RfiMessageFormValues>) => {
            return (
              <Form>
                <SalvTextArea
                  className='m-b-16 flex-grow-1'
                  ariaLabel='Message'
                  placeholder='Reply...'
                  autoSizing={true}
                  onChange={(value: string) => {
                    setFieldValue(`message`, value);
                  }}
                  value={values.message}
                  isInvalid={!!errors.message}
                  minRows={1}
                  maxRows={10}
                />

                {messageAttachments.length != 0 && (
                  <Row>
                    <FileUploadDisplay files={messageAttachments} onRemove={removeFile} />
                  </Row>
                )}

                <Row className='justify-content-between'>
                  <FileUpload
                    onChange={addAttachment}
                    size={Size.MEDIUM}
                    isMulti
                    hideUploadedFile
                    externallyUpdatedFiles={[...messageAttachments]}
                    accept={ACCEPTED_EXTENSION_TYPES}
                  />
                  <div>
                    <SalvButton
                      variant='secondary'
                      size={Size.MEDIUM}
                      disabled={isSubmitting || !isValid}
                      onClick={() => sendAndClose(values, setSubmitting, resetForm)}
                      label='Send and close'
                      className='m-r-8'
                    />

                    <SalvButton
                      type='submit'
                      variant='primary'
                      size={Size.MEDIUM}
                      icon={faPaperPlane}
                      disabled={isSubmitting || !isValid}
                      label='Send'
                    />
                  </div>
                </Row>
              </Form>
            );
          }}
        </Formik>
      </div>
    );
  };

  const title = () => {
    return (
      <>
        {counterpartiesNames(alertRfi)} - {alertRfi.title}
        {isFeatureEnabled(ORGANISATION_FEATURE.BRIDGE_GROUP_CONVERSATIONS, features) &&
          conversation.hasConversationKey &&
          conversation.counterparties.length === 1 && (
            <SalvButton
              className='add-participant-button m-l-8'
              variant='muted'
              size={Size.EXTRA_SMALL}
              onClick={() => setShowAddParticipantModal(true)}
              icon={faPlus}
              label={<FontAwesomeIcon icon={faBuildingColumns} />}
            />
          )}
      </>
    );
  };

  const conclusionFields =
    conversation.type === BridgeApiConversationTypeEnum.SENDER
      ? template?.senderConclusionFields
      : template?.recipientConclusionFields;

  return (
    <div className='thread-side-panel'>
      <SidePanel
        title={title()}
        show={show}
        onHide={() => onComplete(false)}
        actions={
          <div className='d-flex flex-column align-items-end'>
            <div className='caps-xs m-b-4 conv-status'>Conversation status</div>
            <ActionDropdown
              label={capitalizeFirstLetter(conversation.status)}
              className='text-capitalize'
              onChange={async (newStatus: any) => {
                if (newStatus.value === BridgeApiConversationStatus.CLOSED && conclusionFields?.length) {
                  setShowConversationConclusionModal(true);
                } else {
                  await changeStatus(newStatus.key);
                  onComplete(true);
                }
              }}
              options={Object.keys(BridgeApiConversationStatus)
                .filter((status) => {
                  if (status === BridgeApiConversationStatus.OBSERVING) {
                    return isFeatureEnabled(ORGANISATION_FEATURE.BRIDGE_GROUP_CONVERSATIONS, features);
                  }
                  return true;
                })
                .map((key) => ({
                  key,
                  value: key,
                  label: capitalizeFirstLetter(key),
                }))}
              selectedOptionAsLabel
              size={Size.MEDIUM}
            />

            {conversation.status === BridgeApiConversationStatus.CLOSED && conversation.isConcluded !== undefined && (
              <SalvButton
                size={Size.EXTRA_SMALL}
                onClick={() => {
                  if (conversation.isConcluded) {
                    setConclusionEditing(true);
                  }
                  setShowConversationConclusionModal(true);
                }}
                variant='primary'
                label={conversation.isConcluded ? 'Edit conclusion' : 'Conclude'}
                icon={conversation.isConcluded ? faPenToSquare : faCircleCheck}
                className='m-t-12'
              />
            )}
          </div>
        }
        subTitle={
          !isFeatureEnabled(ORGANISATION_FEATURE.SANCTIONS, features) &&
          !isFeatureEnabled(ORGANISATION_FEATURE.MONITORING, features) ? (
            <>ID: {alertRfi.conversationId}</>
          ) : (
            <a
              className='subtitle-link text-s'
              href={
                window.location.href.includes('demo') || window.location.href.includes('test')
                  ? `https://bridge-demo.salv.com/messenger/investigations/${conversation.investigationId}/${alertRfi.conversationId}`
                  : `https://bridge.salv.com/messenger/investigations/${conversation.investigationId}/${alertRfi.conversationId}`
              }
              target='_blank'
              rel='noreferrer'
            >
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} className='m-r-8' />
              ID: {alertRfi.conversationId}
            </a>
          )
        }
        footer={footer()}
      >
        {!loading && (
          <div className='scrollable-container' ref={messagesFeedRef}>
            {/*this keeps messages at the bottom*/}
            <div className='flex-grow-1'></div>
            {messages &&
              messages.map((message: any, index) => {
                return renderMessage(
                  message,
                  template,
                  alertRfi,
                  hasRequestedFields() && conversation.type === BridgeApiConversationTypeEnum.SENDER && index === 0
                );
              })}

            {showStructuredRequest() &&
              conversation.status !== BridgeApiConversationStatus.OBSERVING &&
              template &&
              conversation && (
                <StructuredMessageForm
                  alertRfiId={alertRfi.id}
                  conversation={conversation}
                  template={template}
                  onSubmit={() => onComplete(true)}
                />
              )}
          </div>
        )}

        {loading && (
          <ReactSpinner className='loader-spinner' animation='border' role='status'>
            <span className='visually-hidden'>Loading...</span>
          </ReactSpinner>
        )}
      </SidePanel>
      <AddOrganisationToConversationModal
        conversation={conversation}
        showModal={showAddParticipantModal}
        setShowModal={setShowAddParticipantModal}
        onUpdate={onComplete}
      />

      {messages && messages.length !== 0 && conclusionFields && conclusionFields.length !== 0 && (
        <ConversationConclusionModal
          showModal={showConversationConclusionModal}
          isEditing={isConclusionEditing}
          conversation={conversation}
          conclusionFields={conclusionFields}
          providedAttributes={messages.reduce((acc, message) => {
            const structuredMessage: JsonMessage | undefined = getStructuredMessage(message.content);

            if (
              structuredMessage &&
              [JsonMessageTypeEnum.STRUCTURED_REQUEST_RESPONSE, JsonMessageTypeEnum.STRUCTURED_REQUEST].includes(
                structuredMessage.type
              )
            ) {
              return { ...acc, ...structuredMessage?.attributes };
            }
            return acc;
          }, {})}
          onClose={async () => {
            await changeStatus(BridgeApiConversationStatus.CLOSED);
            setShowConversationConclusionModal(false);
            setConclusionEditing(false);
            onComplete(true);
          }}
        />
      )}
    </div>
  );
};

export default ThreadSidePanel;
